import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import "./prezzi.css";

const DEFAULTS = {
  setup: [], extras: [], bundle: {}, subscriptions: [], addons: [], notes: [],
};

const Price = ({ amount, per }) => (
  <div className="mcs-price">
    <span className="cur">€</span>
    {amount}
    {per && <span className="per">{per}</span>}
  </div>
);

const ctaLink = (label) => `/contatti?interesse=${encodeURIComponent(label)}`;

export default function PrezziSoluzioni() {
  const [period, setPeriod] = useState("m");
  const [data, setData] = useState(DEFAULTS);
  const isYear = period === "y";

  useEffect(() => {
    api.get("/pricing").then((r) => setData({ ...DEFAULTS, ...r.data })).catch(() => {});
  }, []);

  return (
    <section className="mcs-pricing" id="soluzioni-prezzi" data-testid="prezzi-page">
      <div className="mcs-wrap">
        <span className="mcs-eyebrow"><span className="mcs-mono">M<b>C</b></span> · Soluzioni e prezzi</span>
        <h2 className="mcs-h2">Costruiamo il tuo sito. Poi lo teniamo vivo.</h2>
        <p className="mcs-lead">Due passi semplici: scegli come nasce il tuo sito, poi il piano che lo mantiene sicuro, aggiornato e performante. Niente sorprese, niente costi nascosti.</p>

        {/* STRATO 1 */}
        <div className="mcs-layer">
          <div className="mcs-layer-head">
            <span className="mcs-step">1</span>
            <span>
              <span className="mcs-layer-title">Crea il tuo sito</span><br />
              <span className="mcs-layer-sub">Una tantum · hosting primo anno incluso</span>
            </span>
          </div>

          <div className="mcs-grid cols-3">
            {data.setup.map((m, i) => (
              <div key={i} className={`mcs-card${m.hero ? " is-hero" : ""}`} data-testid={`setup-card-${i}`}>
                {m.badge && <span className="mcs-badge">{m.badge}</span>}
                <span className="mcs-tier">{m.tier}</span>
                <h3>{m.title}</h3>
                <Price amount={m.price} />
                <div className="mcs-vat">una tantum · + IVA</div>
                <p className="mcs-desc">{m.desc}</p>
                <ul className="mcs-feats">
                  {(m.feats || []).map((f, k) => <li key={k}>{f}</li>)}
                </ul>
                <Link className="mcs-cta" to={ctaLink(`${m.tier} — ${m.title}`)} data-testid={`setup-cta-${i}`}>{m.cta}</Link>
              </div>
            ))}
          </div>

          <div className="mcs-grid cols-2 mcs-extra">
            {data.extras.map((e, i) => (
              <div key={i} className="mcs-card" data-testid={`extra-card-${i}`}>
                <span className="mcs-tier">{e.tier}</span>
                <h3>{e.title}</h3>
                <Price amount={e.price} />
                <div className="mcs-vat">una tantum · + IVA</div>
                <p className="mcs-desc">{e.desc}</p>
                <Link className="mcs-cta" to={ctaLink(`Servizio extra — ${e.title}`)} data-testid={`extra-cta-${i}`}>Richiedi</Link>
              </div>
            ))}
          </div>

          {data.bundle && data.bundle.name && (
            <div className="mcs-bundle">
              <div>
                <div className="b-name">{data.bundle.name}</div>
                <div className="b-desc">{data.bundle.desc}</div>
              </div>
              <div className="b-right">
                {data.bundle.old && <span className="b-old">{data.bundle.old}</span>}
                <span className="b-price"><span className="cur">€</span>{data.bundle.price}</span>
                <Link className="mcs-cta" to={ctaLink(data.bundle.name)} data-testid="bundle-cta">{data.bundle.cta}</Link>
              </div>
            </div>
          )}
        </div>

        {/* STRATO 2 */}
        <div className="mcs-layer">
          <div className="mcs-layer-head">
            <span className="mcs-step">2</span>
            <span>
              <span className="mcs-layer-title">Mantienilo vivo</span><br />
              <span className="mcs-layer-sub">Abbonamento · parte dal 13° mese</span>
            </span>
            <div className="mcs-toggle" role="tablist" aria-label="Periodicità">
              <button className={!isYear ? "active" : ""} onClick={() => setPeriod("m")} role="tab" aria-selected={!isYear} data-testid="toggle-monthly">Mensile</button>
              <button className={isYear ? "active" : ""} onClick={() => setPeriod("y")} role="tab" aria-selected={isYear} data-testid="toggle-yearly">Annuale</button>
            </div>
            {isYear && <span className="mcs-save">≈ 2 mesi gratis</span>}
          </div>

          <div className="mcs-grid cols-4">
            {data.subscriptions.map((s, i) => (
              <div key={i} className={`mcs-card${s.hero ? " is-hero" : ""}`} data-testid={`sub-card-${i}`}>
                {s.badge && <span className="mcs-badge">{s.badge}</span>}
                <span className="mcs-tier">{s.tier}</span>
                <h3>{s.title}</h3>
                <Price amount={isYear ? s.y : s.m} per={isYear ? "/anno" : "/mese"} />
                <div className="mcs-vat">+ IVA</div>
                <p className="mcs-desc">{s.desc}</p>
                <ul className="mcs-feats">
                  {(s.feats || []).map((f, k) => <li key={k}>{f}</li>)}
                </ul>
                <Link className="mcs-cta" to={ctaLink(s.tier)} data-testid={`sub-cta-${i}`}>{s.cta}</Link>
              </div>
            ))}
          </div>

          {data.addons.length > 0 && (
            <div className="mcs-addons">
              <h4>Aggiungi quando vuoi</h4>
              <div className="mcs-chips">
                {data.addons.map((a, i) => (
                  <span key={i} className="mcs-chip"><b>{a.label}</b> · <span className={a.hot ? "hot" : ""}>{a.value}</span></span>
                ))}
              </div>
            </div>
          )}
        </div>

        {data.notes.length > 0 && (
          <div className="mcs-note">
            {data.notes.map((n, i) => (
              <div key={i}><div className="t">{n.t}</div><div className="d">{n.d}</div></div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
